import type { PropsWithChildren } from 'react';
import { memo, useRef } from 'react';

import { animated, useTransition } from 'react-spring'
import styled from 'styled-components'

export interface ISlideToggleProps {
  show?: boolean
  duration?: number
}

const ContentRef = styled.div`
  &:before,
  &:after {
    content: '';
    display: table;
  }
`

const visibleStyle = { height: 'auto', opacity: 1, overflow: 'visible' }
const hiddenStyle = { opacity: 0, height: 0, overflow: 'hidden' }

function getElementHeight(ref) {
  return ref.current ? ref.current.getBoundingClientRect().height : 0
}

export const SlideToggle = memo(
  ({
    children,
    show = false,
    duration = 150,
  }: PropsWithChildren<ISlideToggleProps>) => {
    const isVisibleOnMount = useRef(show)

    const containerRef = useRef(null)
    const contentRef = useRef<HTMLDivElement>(null)

    const transitions = useTransition<boolean, any>(show, null, {
      config: {
        duration,
      },
      delay: 0,
      enter: () => async (next, cancel) => {
        const height = getElementHeight(contentRef)

        cancel()

        await next({ height, opacity: 1, overflow: 'hidden' })
        await next(visibleStyle)
      },
      leave: () => async (next, cancel) => {
        const height = getElementHeight(containerRef)

        cancel()

        await next({ height, overflow: 'hidden' })
        await next(hiddenStyle)

        isVisibleOnMount.current = false
      },
      from: isVisibleOnMount.current ? visibleStyle : hiddenStyle,
      unique: true,
    })

    return (
      <>
        {transitions.map(({ item: showItem, props: springProps, key }) => {
          if (showItem) {
            return (
              <animated.div ref={containerRef} key={key} style={springProps}>
                <ContentRef ref={contentRef}>{children}</ContentRef>
              </animated.div>
            )
          }

          return null
        })}
      </>
    )
  }
)

SlideToggle.displayName = 'SlideToggle'
